import {GitPullRequestDraftIcon, StopIcon} from '@primer/octicons-react'
import {useAnalytics} from '@github-ui/use-analytics'
import {Button, Flash} from '@primer/react'
import CircleOcticon from '@github-ui/circle-octicon'
import {Octicon} from '@primer/react/deprecated'

import {HEADER_ICON_SIZE} from '../../constants'
import {useMarkReadyForReviewMutation} from '../../hooks/mutations/use-mark-ready-for-review-mutation'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {useId, useState} from 'react'

import styles from './DraftStateSection.module.css'

/**
 *
 * Renders if the pull request is in a Draft State
 */
export function DraftStateSection({viewerCanUpdate, helpUrl}: {viewerCanUpdate: boolean; helpUrl: string}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {mutate: markReadyForReviewMutation, isPending} = useMarkReadyForReviewMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
    },
  })
  const {sendAnalyticsEvent} = useAnalytics()
  const draftStateSectionAriaId = useId()

  function handleMarkReadyForReview() {
    if (isPending) return

    markReadyForReviewMutation()
    sendAnalyticsEvent(
      'draft_state_section.mark_ready_for_review',
      'MERGEBOX_DRAFT_STATE_SECTION_MARK_READY_FOR_REVIEW_BUTTON',
    )
  }

  const url = `${helpUrl}/get-started/learning-about-github/access-permissions-on-github`

  return (
    <section
      aria-label="Draft state"
      aria-describedby={draftStateSectionAriaId}
      className="border-bottom color-border-subtle"
    >
      <MergeBoxSectionHeader
        headerId={draftStateSectionAriaId}
        title="This pull request is still a work in progress"
        subtitle={
          viewerCanUpdate ? (
            'Draft pull requests cannot be merged.'
          ) : (
            <span>
              Only those with <a href={url}>write access</a> to this repository can mark a draft pull request as ready
              for review.
            </span>
          )
        }
        icon={
          <CircleOcticon
            size={HEADER_ICON_SIZE}
            icon={() => <GitPullRequestDraftIcon size={16} />}
            className={styles.DraftStateCircleIcon}
          />
        }
        rightSideContent={
          viewerCanUpdate ? (
            <Button
              onClick={handleMarkReadyForReview}
              loading={isPending}
              loadingAnnouncement="Marking ready for review"
            >
              Ready for review
            </Button>
          ) : undefined
        }
      >
        {errorMessage && (
          <Flash variant="danger" className={styles.DraftStateFlashMessage}>
            <Octicon icon={StopIcon} className={styles.DraftStateIcon} />
            {errorMessage}
          </Flash>
        )}
      </MergeBoxSectionHeader>
    </section>
  )
}
